import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { Form, Field, SubmitButton, Image } from '@bottomless/common/components';
import { saveReferralAction } from '../../../store';
import { trackEvent, EVENT_REFERRAL_CLICK } from '../../../../../src/utils/tracker';
import { Col, Container, Row } from 'reactstrap';
import './CoffeeInvitation.scss';
import { HowItWorks } from '../components/HowItWorks';
import { Reviews } from '../components/Reviews';
import { Faq } from '../components/Faq';

const Schema = Yup.object().shape({
  referral_id: Yup.string().required(),
});

const faqs = [
  {
    question: "How does Bottomless' Subscribe By Usage work?",
    answer:
      "You start by taking a quiz to find your perfect coffee. We'll send you a scale with your first order. Store your coffee on the scale and it will reorder your next bag at the perfect time.",
  },
  {
    question: 'How do I pick my coffee?',
    answer:
      'We have an online store featuring a variety of roasters. If you like consistency, you can pick a specific coffee which will be reordered repeatedly. If you like variety, you can choose one of our Rotating products. You can change the product linked to your account at any time.',
  },
  {
    question: 'How do rotations work?',
    answer:
      'Our rotation engine is the most advanced ever built. You can specify almost any possible coffee configuration. We pick the coffee specifically for you with every order based on your location, preferences and your ratings.',
  },
  {
    question: 'How much does it cost?',
    answer:
      "Membership costs $7.99/month and includes the scale and unlimited shipping. Additionally, you'll get 20% off your first bag and 10% discount off each reorder on a subscription by usage.",
  },
  {
    question:
      'What is the difference between signing up for a subscription with Bottomless, and signing up with a brand directly?',
    answer:
      'Bottomless currently offers only coffee rotations with a monthly membership fee. Signing up for a Bottomless membership directly offers access to a marketplace and rotation of different brands for a monthly fee. Signing up with a brand gives you direct access to one brand at no additional cost.',
  },
  {
    question: 'Do customers have control over ordering?',
    answer:
      "Yes, you will get an SMS or email alert 8-12 hours before reorders are placed so you have a chance to change your product, postpone or cancel. You can also customise how 'eager' our algorithms are to order for you. For example, we can try to optimize for 'never running out', we can 'balance priorities' or we can try to order later to optimize freshness.",
  },
  {
    question: 'Can I use my own container with the scale?',
    answer: "Yes! The scale can easily be 'zeroed' with almost any home coffee container or jar.",
  },
  {
    question: 'I use a grinder and hopper. How does it work then?',
    answer:
      'A ton of our customers do this. It works perfectly fine, just pour less than half a bag into the hopper at a time.',
  },
];

const howItWorks = [
  {
    image: 'https://bottomless.imgix.net/landing-coffee-v2/how-it-works-1.png',
    title: 'Start your subscription',
    description: 'Buy your first bag by selecting a pre-built rotation or building your own',
  },
  {
    image: 'https://bottomless.imgix.net/landing-coffee-v2/how-it-works-2.png',
    title: 'Set up your scale',
    description: 'Receive and set up the Wi-Fi scale. Then, store your coffee on it',
  },
  {
    image: 'https://bottomless.imgix.net/landing-coffee-v2/how-it-works-3.png',
    title: 'Get reorders based on usage',
    description: 'The scale will reorder fresh coffee at the perfect time',
  },
];

const valueProps = [
  {
    title: '50+ roasters',
    description: 'With Bottomless Subscribe By Usage',
  },
  {
    title: '3.5k+ possible rotations',
    description: 'Advanced rotations built based on your preferences',
  },
  {
    title: '2M+ reorders',
    description: 'Triggered by Bottomless scales',
  },
];

const reviews = [
  {
    text:
      "The Wi-Fi enabled scale lets Bottomless know when I'm running low on coffee and I get a text letting me know that coffee is about to be ordered. That allows me to switch my order if I want. I would highly recommend.",
    author: 'sbw86',
    source: 'Reddit review',
    image: 'https://bottomless.imgix.net/landing-coffee-v2/review-1.png',
  },
  {
    text:
      "Y'all have the best service for a coffee subscription, hands down. The subscription model you use is objectively better, and your support team is AMAZING.",
    author: 'Joseph',
    source: 'Coffee customer',
    image: 'https://bottomless.imgix.net/landing-coffee-v2/review-2.png',
  },
  {
    text:
      "With the help of Bottomless I get to explore different flavors from different regions without having to leave the house..😍 In fact, I don't even have to choose, I just put in my preferences; ie roast, flavor, even price, and it will select a variety of different coffees every time the scale is low.",
    author: 'Patrick',
    source: 'Coffee customer',
    image: 'https://bottomless.imgix.net/landing-coffee-v2/review-3.png',
  },
];

const CoffeeInvitationComponent = ({ referrer, saveReferralId, isCoffee }) => {
  const history = useHistory();

  const onSuccessGetStarted = data => {
    trackEvent(EVENT_REFERRAL_CLICK, data, {});
    history.push('/filter_checkout');
    window.location.reload();
  };

  const onSuccessShopNow = data => {
    trackEvent(EVENT_REFERRAL_CLICK, data, {});
    history.push('/open_shop');
    window.location.reload();
  };

  const discountText = useMemo(() => {
    let text = `Buy your first bag today`;

    if (referrer?.pricing_rule?.first_bag_discount) {
      text += ` at ${referrer?.pricing_rule?.first_bag_discount * 100}% off`;
    }

    text += `, get your second bag on us`;
    text += referrer?.pricing_rule?.discount_proportion
      ? `, and enjoy ${referrer?.pricing_rule?.discount_proportion * 100}% off all future bags.`
      : '.';

    return text;
  }, [referrer?.pricing_rule]);

  return (
    <>
      <div className="splash-wrapper section section-splash section-white">
        <Container>
          <Row className="align-items-center referral-invite">
            <Col xs="12" md="7 mb-5 mb-md-0 text-center text-md-left">
              <h1 className="mb-4">
                Hey! Your friend <span className="text-primary font-italic">{referrer.name}</span>
                <br />
                thinks you&apos;ll love Bottomless
              </h1>
              {discountText && <div className="discount-text mb-4 pb-2">{discountText}</div>}
              <div className="d-flex justify-content-center justify-content-md-start mb-4">
                <Form
                  initialValues={{ referral_id: referrer.referral_id }}
                  validationSchema={Schema}
                  onSubmit={saveReferralId}
                  onSuccess={onSuccessGetStarted}
                  className="mr-3"
                >
                  {({ isSubmitting }) => (
                    <div className="d-flex justify-content-center">
                      <Field name="referral_id" type="hidden" />
                      <SubmitButton
                        color="primary"
                        size="lg"
                        isSubmitting={isSubmitting}
                        loadingText="Signing up"
                        className="hero-button button-primary w-inline-block"
                        withLoader={false}
                      >
                        Build a rotation
                      </SubmitButton>
                    </div>
                  )}
                </Form>
                <Form
                  initialValues={{ referral_id: referrer.referral_id }}
                  validationSchema={Schema}
                  onSubmit={saveReferralId}
                  onSuccess={onSuccessShopNow}
                >
                  {({ isSubmitting }) => (
                    <div className="d-flex justify-content-center">
                      <Field name="referral_id" type="hidden" />
                      <SubmitButton
                        size="lg"
                        color="dark"
                        outline
                        isSubmitting={isSubmitting}
                        loadingText="Signing up"
                        withLoader={false}
                      >
                        Shop coffee
                      </SubmitButton>
                    </div>
                  )}
                </Form>
              </div>
              {referrer?.pricing_rule?.description && <div className="pt-2">{referrer?.pricing_rule?.description}</div>}
            </Col>
            <Col xs="12" md="5" className="text-center text-md-right">
              <Image src="/referral-v2/hero.png" alt="Coffee" width="378" />
            </Col>
          </Row>
        </Container>
      </div>
      {isCoffee && (
        <>
          <HowItWorks howItWorks={howItWorks} />
          <Reviews valueProps={valueProps} reviews={reviews} />
          <Faq faqs={faqs} />
        </>
      )}
    </>
  );
};

CoffeeInvitationComponent.propTypes = {
  referrer: PropTypes.object.isRequired,
  saveReferralId: PropTypes.func.isRequired,
  isCoffee: PropTypes.bool.isRequired,
};

export const CoffeeInvitation = withRouter(
  connect(
    ({ user }) => ({ me: user.me }),
    dispatch => ({
      saveReferralId: data => dispatch(saveReferralAction(data)),
    })
  )(CoffeeInvitationComponent)
);
